import { ModalWindow } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackCloseNoLinksPopup, trackRemoveFromSpamClick, trackResendErrorMessageClick } from './tracking';
import { useForbiddenTextPopupHandler } from './useForbiddenTextPopupHandler';
import { setEditableMessage } from '../../actions/chat/actions';
import { removeFromArchive, removeFromSpam } from '../../actions/chatOperations';
import { Alert } from '../../components/Alert';
import { NoForbiddenTextPopup } from '../../components/NoForbiddenTextPopup';
import { NoLinksPopup } from '../../components/NoLinksPopup';
import {
  chatListSelector,
  currentChatOpponentIdSelector,
  currentOpenChatSelector,
  hostTypeSelector,
} from '../../selectors';
import { alertSelector } from '../../selectors/alertSelector';
import { hideAlert } from '../../slices/alertSlice';
import { EAlert } from '../../types/alert';
import { EHostType } from '../../types/hostType';
import { useApplicationContext } from '../../utils/applicationContext';
import { isWebView } from '../../utils/isWebView';

export const AlertContainer: React.FC = React.memo(() => {
  const dispatch = useDispatch();

  const { config } = useApplicationContext();
  const { movedToArchiveChatIds, movedToSpamChatIds } = useSelector(chatListSelector);
  const { isVisible, type, message, chatId, modalWarning, button } = useSelector(alertSelector);
  const opponentId = useSelector(currentChatOpponentIdSelector);
  const chat = useSelector(currentOpenChatSelector);
  const hostType = useSelector(hostTypeSelector);

  const isIframe = hostType === EHostType.Frame;
  const isWebview = isWebView(config);

  const onCloseHandler = React.useCallback(() => dispatch(hideAlert()), [dispatch]);

  const removeFromArchiveHandler = React.useCallback(() => {
    dispatch(removeFromArchive(movedToArchiveChatIds[movedToArchiveChatIds.length - 1]));
    dispatch(hideAlert());
    trackRemoveFromSpamClick('archive', opponentId);
  }, [dispatch, movedToArchiveChatIds, opponentId]);

  const removeFromSpamHandler = React.useCallback(() => {
    const chatId = movedToSpamChatIds[movedToSpamChatIds.length - 1];

    dispatch(removeFromSpam(chatId));
    dispatch(hideAlert());
    trackRemoveFromSpamClick('spam', opponentId);
  }, [dispatch, movedToSpamChatIds, opponentId]);

  const resendErrorMessage = React.useCallback(() => {
    dispatch(setEditableMessage({ text: message as string, chatId: chatId as string }));
    dispatch(hideAlert());
    trackResendErrorMessageClick(chat, opponentId);
  }, [chat, chatId, dispatch, message, opponentId]);

  const closeNoLinksPopup = React.useCallback(() => {
    dispatch(setEditableMessage({ text: '', chatId: chatId as string }));
    dispatch(hideAlert());
    trackCloseNoLinksPopup(chat, opponentId);
  }, [chat, chatId, dispatch, opponentId]);

  const { handleCloseNoForbiddenTextPopup, handleClickNoForbiddenTextPopupLink, isMobile } =
    useForbiddenTextPopupHandler({
      message,
      chatId,
      chat,
      opponentId,
    });

  if (type === 'spam') {
    return (
      <Alert
        isError={false}
        isVisible={isVisible}
        withOffset={!isWebview && !isIframe}
        message="Чат заблокирован"
        onClose={onCloseHandler}
        onCancel={movedToSpamChatIds.length > 0 ? removeFromSpamHandler : undefined}
      />
    );
  }

  if (type === 'archive') {
    const message = movedToArchiveChatIds.length > 1 ? 'Чаты перенесены в архив' : 'Чат перенесён в архив';

    return (
      <Alert
        isError={false}
        isVisible={isVisible}
        withOffset={!isWebview && !isIframe}
        message={message}
        onClose={onCloseHandler}
        onCancel={movedToArchiveChatIds.length > 0 ? removeFromArchiveHandler : undefined}
      />
    );
  }

  if (type === EAlert.MessageHasLinkToAnotherService) {
    return (
      <ModalWindow
        open
        content={<NoLinksPopup onClick={resendErrorMessage} />}
        onClose={closeNoLinksPopup}
        size="M"
        fixed={false}
      />
    );
  }

  if (type === EAlert.MessageСontainsForbiddenText && modalWarning) {
    return (
      <NoForbiddenTextPopup
        isMobile={isMobile}
        modalWarning={modalWarning}
        handleClose={handleCloseNoForbiddenTextPopup}
        handleClickLinkButton={handleClickNoForbiddenTextPopupLink}
      />
    );
  }

  return (
    <Alert
      isError={type !== 'info'}
      isVisible={isVisible}
      withOffset={!isWebview && !isIframe}
      onClose={onCloseHandler}
      message={message || 'Сервисная ошибка. Повторите попытку'}
      button={button}
    />
  );
});

AlertContainer.displayName = 'AlertContainer';
